import { useContext, useEffect, useRef } from 'react';
import { socketIo } from 'context/socket';

export const PRICE_REQUEST_UPDATED_EVENT = 'PRICE_REQUEST_UPDATED';

/**
 * The request id carried by a PRICE_REQUEST_UPDATED event. socket-server sends
 * { eventName, priceRequestId, organizationIds: [chainId, labId] }; a bare id or a
 * { data } wrapper (as other socket events use) is accepted too.
 */
export const getUpdatedPriceRequestId = (payload: unknown): string | undefined => {
    if (typeof payload === 'string') return payload;
    if (!payload || typeof payload !== 'object') return undefined;
    const { data, priceRequestId, _id } = payload as Record<string, unknown>;
    if (typeof priceRequestId === 'string') return priceRequestId;
    if (typeof _id === 'string') return _id;
    return data === undefined ? undefined : getUpdatedPriceRequestId(data);
};

/**
 * DD-3697: calls onUpdated when the socket says this request changed (status, lines or a
 * new message). The event is a hint: callers refetch, they never write the cache from it.
 */
export const usePriceRequestUpdated = (priceRequestId: string, onUpdated: () => void): void => {
    const socket = useContext(socketIo.getSocketContext());
    const onUpdatedRef = useRef(onUpdated);
    onUpdatedRef.current = onUpdated;

    useEffect(() => {
        if (!socket) return undefined;
        const handler = (payload: unknown) => {
            if (getUpdatedPriceRequestId(payload) === priceRequestId) {
                onUpdatedRef.current();
            }
        };
        socket.on(PRICE_REQUEST_UPDATED_EVENT, handler);
        return () => {
            // Only this handler: other components may listen to the same event.
            socket.off(PRICE_REQUEST_UPDATED_EVENT, handler);
        };
    }, [socket, priceRequestId]);
};
